/**
 * FeedbackListPage - 피드백 페이지
 *
 * 다른 페이지(AiMemoPage 등)와 동일한 레이아웃 패턴:
 * - 루트: flex flex-col h-full + md:rounded-panel + md:border
 * - header: 고정 헤더 (햄버거 메뉴 + 제목 + 작성/설정 버튼)
 * - main: 스크롤 가능한 본문 영역
 *
 * 기능:
 * - "내 피드백" 리스트 (상태별 필터)
 * - "작성" 버튼 → FeedbackFormModal
 * - 설정: 디버깅 모드 토글 → ScreenshotFloatingButton 활성화
 */

import { useEffect, useState } from 'react';
import { Trans, t } from '@lingui/macro';
import { Bug, Lightbulb, ChevronRight, Plus, Loader2, Settings, Menu as MenuIcon, Images, Camera, MessageSquare, Trash2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Switch } from '../../components/ui/switch';
import { useToast } from '../../components/ui/use-toast';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';

import { Fire } from '../../services/FireClient';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import { useAppUIContext } from '../../context/AppUIContext';
import type { Feedback, FeedbackStatus } from '../../models/Feedback';
import { FEEDBACK_STATUS_LABELS } from '../../models/Feedback';
import { i18n } from '@lingui/core';
import { FeedbackFormModal } from './FeedbackFormModal';
import { FeedbackDetailModal } from './FeedbackDetailPage';
import { FeedbackDraftImagesModal } from './FeedbackDraftImagesModal';
import type { DraftImage } from '../../models/Feedback';

const STATUS_FILTERS: Array<{ key: FeedbackStatus | 'all'; label: string }> = [
  { key: 'all', label: 'All' },
  { key: 'draft', label: 'Draft' },
  { key: 'submitted', label: '제출완료' },
  { key: 'reviewing', label: '검토중' },
  { key: 'resolving', label: '해결중' },
  { key: 'unresolved', label: '미해결' },
  { key: 'resolved', label: '해결완료' },
];

const STATUS_COLORS: Record<FeedbackStatus, string> = {
  draft: 'bg-slate-500/10 text-slate-600',
  submitted: 'bg-blue-500/10 text-blue-600',
  reviewing: 'bg-yellow-500/10 text-yellow-600',
  resolving: 'bg-orange-500/10 text-orange-600',
  unresolved: 'bg-red-500/10 text-red-600',
  resolved: 'bg-green-500/10 text-green-600',
};

const DEBUG_MODE_KEY = 'feedback-debug-mode';

export default function FeedbackListPage() {

  const { setIsMobileMenuOpen } = useAppUIContext();
  const [feedbacks, setFeedbacks] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<FeedbackStatus | 'all'>('all');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedFeedbackId, setSelectedFeedbackId] = useState<string | null>(null);
  const [editingDraft, setEditingDraft] = useState<Feedback | null>(null);
  const [showSettings, setShowSettings] = useState(false);
  const [debugMode, setDebugMode] = useState(() =>
    localStorage.getItem(DEBUG_MODE_KEY) === 'true'
  );
  const [counter, setCounter] = useState(0);

  // Draft Images
  const [isDraftModalOpen, setIsDraftModalOpen] = useState(false);
  const [draftImageCount, setDraftImageCount] = useState(0);
  const [draftsForNewFeedback, setDraftsForNewFeedback] = useState<DraftImage[]>([]);

  // 임시저장 삭제 확인 다이얼로그용
  const [feedbackToDelete, setFeedbackToDelete] = useState<Feedback | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    const unsub = MyNotifs.subscribeToNotificationUpdates(() => {
      setCounter(MyNotifs.NrOfNew);
    });
    return () => unsub();
  }, []);

  useEffect(() => {
    if (!My.isLoggedIn()) return;
    const unsubFeedbacks = Fire.feedback.subscribeUserFeedbacks(My.user.id, (list) => {
      setFeedbacks(list);
      setLoading(false);
    });
    const unsubDrafts = Fire.feedback.subscribeDraftImages(My.user.id, (imgs) => {
      setDraftImageCount(imgs.length);
    });

    return () => {
      unsubFeedbacks();
      unsubDrafts();
    };
  }, []);

  const toggleDebugMode = () => {
    const next = !debugMode;
    setDebugMode(next);
    localStorage.setItem(DEBUG_MODE_KEY, String(next));
    window.dispatchEvent(new Event('debug-mode-changed'));
  };

  const confirmDeleteDraft = async () => {
    if (!feedbackToDelete) return;
    setIsDeleting(true);
    try {
      await Fire.feedback.deleteFeedback(feedbackToDelete.id);
      toast({ title: t`Draft deleted` });
      setFeedbackToDelete(null);
    } catch (err) {
      console.error('Failed to delete draft feedback:', err);
      toast({ variant: 'destructive', title: t`Failed to delete draft` });
    } finally {
      setIsDeleting(false);
    }
  };

  const filtered = filter === 'all'
    ? feedbacks
    : feedbacks.filter(f => f.status === filter);

  return (
    <div className="flex flex-col h-full overflow-hidden bg-background md:rounded-panel md:border md:border-border">
      {/* ── Header ── */}
      <header className="z-40 bg-background border-b min-w-0 flex items-center justify-between px-4 py-4 shrink-0 shadow-sm">
        <div className="flex items-center gap-2">
          {/* 모바일 햄버거 */}
          <div className="shrink-0 lg:hidden">
            <Button
              variant="ghost"
              size="icon"
              data-cy="menu"
              onClick={() => setIsMobileMenuOpen(true)}
              className="relative"
            >
              <MenuIcon className="h-5 w-5" />
              {!!counter && (
                <Badge variant="destructive" className="absolute -top-1 -right-1 h-5 w-5 flex items-center justify-center p-0 rounded-full text-meta-sm">
                  {counter}
                </Badge>
              )}
            </Button>
          </div>

          <div className="flex-1 min-w-0">
            <h1 className="text-lg font-semibold truncate"><Trans>Feedback</Trans></h1>
          </div>
        </div>

        <div className="flex items-center gap-2">

          <Button
            variant="ghost"
            size="icon"
            onClick={() => setShowSettings(!showSettings)}
            className={showSettings ? 'bg-muted' : ''}
          >
            <Settings className="w-4 h-4" />
          </Button>
          <Button onClick={() => setIsFormOpen(true)} size="sm">
            <Plus className="w-4 h-4 mr-1" />
            <Trans>Write</Trans>
          </Button>
        </div>
      </header>

      {/* ── Main Content ── */}
      <main className="flex-1 overflow-hidden bg-background">
        <div className="h-full overflow-y-auto">
          <div className="max-w-3xl mx-auto p-4 sm:p-6 space-y-5">

            {/* 설정 패널 */}
            {showSettings && (
              <div className="rounded-xl border border-border bg-card p-4 space-y-3">
                <h3 className="text-sm font-semibold text-foreground flex items-center gap-2">
                  <Settings className="w-4 h-4" />
                  <Trans>Settings</Trans>
                </h3>
                <div className="flex items-center justify-between">
                  <div>
                    <div className="text-sm font-medium text-foreground">
                      <Trans>Debug Mode</Trans>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      <Trans>Enable floating screenshot capture tool</Trans>
                    </div>
                  </div>
                  <Switch
                    checked={debugMode}
                    onCheckedChange={toggleDebugMode}
                  />
                </div>
              </div>
            )}

            {/* Draft 이미지 배너 — 저장된 스크린샷이 있을 때만 표시 */}
            {draftImageCount > 0 && (
              <button
                onClick={() => setIsDraftModalOpen(true)}
                className="w-full flex items-center gap-3 rounded-xl border border-primary/30 bg-primary/5 hover:bg-primary/10 p-3 transition-colors group"
              >
                <div className="shrink-0 flex items-center justify-center w-10 h-10 rounded-lg bg-primary/10">
                  <Camera className="w-5 h-5 text-primary" />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <p className="text-sm font-semibold text-foreground">
                    <Trans>Draft Screenshots</Trans>
                    <Badge variant="default" className="ml-2 text-meta-sm min-w-5 h-5 px-1 py-0 rounded-full inline-flex items-center justify-center">
                      {draftImageCount}
                    </Badge>
                  </p>
                  <p className="text-xs text-muted-foreground">
                    <Trans>Tap to view or attach to feedback</Trans>
                  </p>
                </div>
                <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity" />
              </button>
            )}

            {/* 필터 */}
            <div className="flex gap-2 overflow-x-auto pb-1">
              {STATUS_FILTERS.map(({ key }) => (
                <button
                  key={key}
                  onClick={() => setFilter(key)}
                  className={`px-3 py-1.5 rounded-full text-sm font-medium whitespace-nowrap transition-colors
                    ${filter === key
                      ? 'bg-primary text-primary-foreground'
                      : 'bg-muted text-muted-foreground hover:bg-muted/80'
                    }`}
                >
                  {key === 'all' ? <Trans>All</Trans> : i18n._(FEEDBACK_STATUS_LABELS[key])}
                  {key !== 'all' && (
                    <span className="ml-1.5 opacity-70">
                      {feedbacks.filter(f => f.status === key).length}
                    </span>
                  )}
                </button>
              ))}
            </div>

            {/* 피드백 목록 */}
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-muted-foreground" />
              </div>
            ) : filtered.length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <Trans>No feedback found</Trans>
              </div>
            ) : (
              <div className="space-y-3">
                {filtered.map(fb => (
                  <FeedbackCard
                    key={fb.id}
                    feedback={fb}
                    onClick={() => {
                      if (fb.status === 'draft') {
                        setEditingDraft(fb);
                        setIsFormOpen(true);
                      } else {
                        setSelectedFeedbackId(fb.id);
                      }
                    }}
                    onDelete={fb.status === 'draft' ? () => setFeedbackToDelete(fb) : undefined}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* 새 피드백 모달 */}
      {isFormOpen && (
        <FeedbackFormModal
          open={isFormOpen}
          onOpenChange={(open) => {
            setIsFormOpen(open);
            if (!open) {
              setDraftsForNewFeedback([]);
              setEditingDraft(null);
            }
          }}
          preAttachedDraftImages={draftsForNewFeedback}
          existingDraft={editingDraft}
        />
      )}

      {/* 피드백 상세 모달 */}
      {selectedFeedbackId && (
        <FeedbackDetailModal
          feedbackId={selectedFeedbackId}
          open={true}
          onOpenChange={(open) => { if (!open) setSelectedFeedbackId(null); }}
        />
      )}

      {/* Draft Images 갤러리 모달 */}
      {isDraftModalOpen && (
        <FeedbackDraftImagesModal
          open={true}
          onOpenChange={(open) => { if (!open) setIsDraftModalOpen(false); }}
          onCreateFeedback={(images) => {
            setDraftsForNewFeedback(images);
            setIsDraftModalOpen(false);
            setIsFormOpen(true);
          }}
        />
      )}

      {/* 임시저장 삭제 확인 다이얼로그 */}
      <AlertDialog
        open={!!feedbackToDelete}
        onOpenChange={(open) => { if (!open && !isDeleting) setFeedbackToDelete(null); }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle><Trans>Delete draft?</Trans></AlertDialogTitle>
            <AlertDialogDescription>
              <Trans>This draft will be permanently deleted. This action cannot be undone.</Trans>
              {feedbackToDelete?.title && (
                <span className="block mt-2 font-medium text-foreground">
                  {feedbackToDelete.title}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>
              <Trans>Cancel</Trans>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); confirmDeleteDraft(); }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isDeleting
                ? <><Loader2 className="w-4 h-4 mr-1 animate-spin" /><Trans>Deleting...</Trans></>
                : <Trans>Delete</Trans>
              }
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </div>
  );
}

// ── 개별 피드백 카드 ──
function FeedbackCard({
  feedback,
  onClick,
  onDelete,
}: {
  feedback: Feedback;
  onClick: () => void;
  onDelete?: () => void;
}) {
  const isBug = feedback.type === 'bug';
  const statusLabel = i18n._(FEEDBACK_STATUS_LABELS[feedback.status]);

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={onClick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
      className="relative w-full text-left rounded-xl border border-border bg-card p-4 hover:bg-accent/50 transition-colors group cursor-pointer focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2"
    >
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 p-1.5 rounded-lg ${isBug ? 'bg-red-500/10' : 'bg-yellow-500/10'}`}>
          {isBug
            ? <Bug className="w-4 h-4 text-red-500" />
            : <Lightbulb className="w-4 h-4 text-yellow-500" />
          }
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2">
            <h3 className="font-semibold text-foreground truncate">{feedback.title}</h3>
            <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[feedback.status]}`}>
              {statusLabel}
            </span>
          </div>

          {feedback.description && (
            <p className="text-sm text-muted-foreground line-clamp-2">{feedback.description}</p>
          )}

          {feedback.tags?.length > 0 && (
            <div className="flex flex-wrap gap-1">
              {feedback.tags.map((tag, i) => (
                <span key={i} className="px-1.5 py-0.5 rounded text-meta-sm font-medium bg-muted text-muted-foreground">
                  {tag}
                </span>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 mt-2">
            <span className="text-xs text-muted-foreground">{new Date(feedback.createdAt).toLocaleDateString()}</span>
            {feedback.screenshots.length > 0 && (
              <span className="text-xs text-muted-foreground flex items-center gap-1">
                <Camera className="w-3.5 h-3.5" /> {feedback.screenshots.length}
              </span>
            )}
            {feedback.messages.length > 0 && (
              <span className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-600 text-meta-sm font-semibold tracking-wide">
                <MessageSquare className="w-3.5 h-3.5" />
                {feedback.messages.length}
              </span>
            )}
          </div>
        </div>

        {onDelete ? (
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              onDelete();
            }}
            aria-label={t`Delete draft`}
            className="shrink-0 p-1.5 rounded-md text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-colors"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        ) : (
          <ChevronRight className="w-4 h-4 text-muted-foreground opacity-0 group-hover:opacity-100 transition-opacity mt-1" />
        )}
      </div>
    </div>
  );
}
